import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { shopApi } from "../lib/shop";
import { useCustomerAuth } from "../context/CustomerAuthContext";

const SCRIPT_SRC = "https://accounts.google.com/gsi/client";

const loadGsi = () => new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
        existing.addEventListener("load", resolve);
        existing.addEventListener("error", reject);
        return undefined;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.append(script);
    return undefined;
});

/** Google One Tap — returning visitors sign in with a single click, no redirect. */
export const GoogleOneTap = () => {
    const { customer, loading, setCustomer } = useCustomerAuth();
    const { data: conf } = useQuery({
        queryKey: ["google-config"],
        queryFn: async () => (await shopApi.get("/auth/google/config")).data,
        staleTime: 10 * 60 * 1000,
    });

    useEffect(() => {
        if (loading || customer || !conf?.enabled || !conf?.client_id) return;
        const path = window.location.pathname;
        if (path.startsWith("/rhm") || path.startsWith("/admin") || path.startsWith("/auth/")) return;
        if (sessionStorage.getItem("rhm_one_tap_dismissed")) return;
        let cancelled = false;
        loadGsi().then(() => {
            if (cancelled || !window.google?.accounts?.id) return;
            window.google.accounts.id.initialize({
                client_id: conf.client_id,
                callback: async ({ credential }) => {
                    try {
                        const { data } = await shopApi.post("/auth/google/onetap", { credential });
                        localStorage.setItem("rhm_customer_token", data.token);
                        setCustomer(data.customer);
                        toast.success(`Signed in as ${data.customer.name || data.customer.email}`);
                    } catch {
                        toast.error("Google sign-in could not be completed");
                    }
                },
                cancel_on_tap_outside: false,
                auto_select: false,
            });
            window.google.accounts.id.prompt((notification) => {
                if (notification.isSkippedMoment?.() || notification.isDismissedMoment?.()) {
                    sessionStorage.setItem("rhm_one_tap_dismissed", "1");
                }
            });
        }).catch(() => undefined);
        return () => { cancelled = true; };
    }, [conf, customer, loading, setCustomer]);

    return null;
};

export default GoogleOneTap;
